var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProjectStatus } from 'root/src/shared/@types/project';
import { Platform } from 'root/src/shared/@types/platforms';
import { ScopeSet } from 'root/src/shared/@types/auth';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useCreateProjectDropMutation } from 'root/src/client/v2/drop/hooks/useCreateProjectDropMutation';
import { useInitialRewardsQuery } from 'root/src/client/v2/drop/hooks/useInitialRewardQuery';
import { useUserDropEventLandingShortUrlQuery } from 'root/src/client/v2/drop/hooks/useDropEventLandingShortUrlQuery';
import { useKeySelectionForm } from 'root/src/client/v2/drop/features/KeySelectionModal/hooks/useKeySelectionModal';
import { RedesignPendingProjectModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignPendingProjectModal';
import { RedesignInitialProductDisplayModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignInitialProductDisplayModal';
import { RedesignChatPermissionModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignChatPermissionModal';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { toast as redesignToast } from 'root/src/client/v2/common/components/ui/sonner';
import { RedesignNoInitialKeysModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignNoInitialKeysModal';
// Redesign-specific fork of useKeySelectionModal: same submit flow, but surfaces feedback the
// redesign way (sonner toast + Button loading/success/error via useAsyncAction) instead of the
// legacy react-hot-toast. The legacy useKeySelectionModal is intentionally left untouched (A/B).
const PRODUCT_POLLING_TIME = 3000;
const getFormDefaultValues = (dropEvent) => {
    var _a;
    const { initialProduct = [] } = dropEvent;
    return {
        userHasGame: false,
        productId: (_a = initialProduct.at(0)) === null || _a === void 0 ? void 0 : _a.id,
    };
};
const isLegacyError = (err) => (typeof err === 'object' && err !== null && 'error' in err);
// The thunk tags the errors it wants handled rather than toasted with a `code`.
const hasErrorCode = (err, code) => (typeof err === 'object' && err !== null && 'code' in err && err.code === code);
export function useRedesignKeySelectionModal(params) {
    var _a;
    const { dropEvent, dare, payload } = params;
    const isSingleProduct = ((_a = dropEvent.initialProduct) !== null && _a !== void 0 ? _a : []).length === 1;
    const form = useKeySelectionForm({
        defaultValues: isSingleProduct ? getFormDefaultValues(dropEvent) : undefined,
    });
    const createProjectDropMutation = useCreateProjectDropMutation();
    const setChatLinkSharingMutation = trpc.project.setChatLinkSharing.useMutation();
    const userLandingShortUrlQuery = useUserDropEventLandingShortUrlQuery(dropEvent.id, { enabled: false });
    const utils = trpc.useUtils();
    const initialRewardsQuery = useInitialRewardsQuery(dropEvent.id, { enabled: false });
    const { show: showModal, hide: hideModal } = useRedesignModal();
    const { loading, success, error, run } = useAsyncAction();
    const initialProductPollingQuery = useQuery({
        queryKey: ['initialProduct', 'polling'],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const res = yield initialRewardsQuery.refetch();
            if (res.isError) {
                throw new Error(res.error.message);
            }
            const items = res.data;
            if (!(items === null || items === void 0 ? void 0 : items.length)) {
                throw new Error('No initial product found, please contact support');
            }
            return items;
        }),
        enabled: false,
        refetchInterval: PRODUCT_POLLING_TIME,
        retry: 30,
    });
    useEffect(() => {
        if (isSingleProduct) {
            form.reset(getFormDefaultValues(dropEvent));
        }
    }, [isSingleProduct]);
    const getProducts = () => __awaiter(this, void 0, void 0, function* () {
        var _b, _c;
        const response = yield initialProductPollingQuery.refetch();
        if (response.isError) {
            throw new Error(response.error.message);
        }
        return (_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.flatMap(item => item.rewards)) !== null && _c !== void 0 ? _c : [];
    });
    const getChosenInitialProductIndex = (values) => {
        var _a;
        if (values.userHasGame) {
            return undefined;
        }
        const index = (_a = dropEvent.initialProduct) === null || _a === void 0 ? void 0 : _a.findIndex(product => product.id === values.productId);
        if (index === undefined || index === -1) {
            throw new Error('Unsupported initial product', { cause: { productId: values.productId } });
        }
        return index;
    };
    // Post-create UI for a successful accept: show pending / close / display the granted key.
    const showCreatedResult = (project, values) => __awaiter(this, void 0, void 0, function* () {
        if (project.status === ProjectStatus.Pending) {
            showModal(<RedesignPendingProjectModal />);
            return;
        }
        if (values.userHasGame) {
            hideModal();
            return;
        }
        const products = yield getProducts();
        const shortUrlQuery = yield userLandingShortUrlQuery.refetch();
        if (shortUrlQuery.isError) {
            throw new Error(shortUrlQuery.error.message);
        }
        const onEnableChatSharing = () => __awaiter(this, void 0, void 0, function* () {
            const hasValidScope = yield utils.auth.hasValidTokenScope.fetch({
                platform: Platform.Twitch,
                scopeSet: ScopeSet.creatorPlus,
            });
            if (hasValidScope) {
                yield setChatLinkSharingMutation.mutateAsync({ dropId: dropEvent.id, enabled: true, intervalMode: 'auto' });
                hideModal();
            }
            else {
                showModal(<RedesignChatPermissionModal onGranted={() => __awaiter(this, void 0, void 0, function* () {
                        yield setChatLinkSharingMutation.mutateAsync({ dropId: dropEvent.id, enabled: true, intervalMode: 'auto' });
                        hideModal();
                    })} onCancel={hideModal}/>);
            }
        });
        showModal(<RedesignInitialProductDisplayModal 
        // TODO: Fix typing
        // @ts-ignore
        dropEvent={dropEvent} shortUrl={shortUrlQuery.data} products={products} dare={dare} linkMarketingCopy={dropEvent.linkMarketingCopy} onEnableChatSharing={onEnableChatSharing}/>, { closeable: false });
    });
    const onFormSubmit = form.handleSubmit((values) => {
        // `run` drives the submit button state. We manage feedback manually (loading -> success/error
        // toast) so the depleted-key case can open a confirm modal instead of a dead-end error toast.
        void run(() => __awaiter(this, void 0, void 0, function* () {
            const chosenInitialProductIndex = getChosenInitialProductIndex(values);
            const submitPayload = Object.assign(Object.assign({}, payload), { chosenInitialProductIndex, userHasGame: values.userHasGame });
            const toastId = redesignToast.loading('Creating your dare...');
            try {
                const project = yield createProjectDropMutation.mutateAsync(submitPayload);
                yield showCreatedResult(project, values);
                redesignToast.success('Dare created', { id: toastId });
            }
            catch (caught) {
                if (hasErrorCode(caught, 'EmailConfirmationCancelled')) {
                    // The creator chose to change their email and is being routed to that page, which clears
                    // this modal on its own. Nothing failed, so say nothing.
                    redesignToast.dismiss(toastId);
                    return;
                }
                if (hasErrorCode(caught, 'NotEnoughInitialProducts')) {
                    // The pool emptied between the drop page's pre-accept check and this submit, so the dare was
                    // not created. The gate enrols the creator on the waitlist as it turns them away, hence
                    // `isWaiting`: there is nothing left for them to opt into here.
                    //
                    // No `formData`, so no "I already own this game" button: this creator reached the picker and
                    // chose a key over that option, and re-offering it here only nudges them to misdeclare. The
                    // modal offers it where the picker was skipped and the choice was never on the table.
                    redesignToast.dismiss(toastId);
                    showModal(<RedesignNoInitialKeysModal dropId={dropEvent.id} isWaiting/>);
                    return;
                }
                redesignToast.error(isLegacyError(caught) ? caught.error : caught instanceof Error ? caught.message : 'Dare cannot be created', { id: toastId });
                throw caught;
            }
        }), {
            // Success transitions to the pending / product modal, so no lingering tick.
            successHold: 0,
        }).catch(() => {
            // `run` without a `toast` re-throws real failures; we already surfaced the error toast and
            // the button error state above, so swallow the rejection to avoid an unhandled promise.
        });
    });
    return { form, onFormSubmit, loading, success, error };
}
